<script lang="ts" setup>
import { TitledLayout } from '@/layouts';
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmNotificationGroup,
  TirPmScrollbar,
  TirPmSkeletonLoader,
} from 'tir-components';
import {
  computed,
  nextTick,
  onBeforeMount,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from 'vue';
import { ArrowUturnLeftIcon } from 'tir-style-system/icons/outline';
import { TasksOrderPagesAdapterService, UpdateTaskAdapterService } from '../adapter-service';
import { TeammateCard } from './components';
import {
  RemoveTaskResponseEventModel,
  UpsertTaskOrderForUserEventResponse,
  ZmkTaskByPerformerListRequest,
} from '../models';
import { useRouter } from 'vue-router';
import { EmployeeMultiselect, TaskDrawer, TaskDrawerSettingsModel } from '@/components';
import EnumUserStatus from '@/api/Main/dataSource/api/users/base/enums/EnumUserStatus';
import TasksKanbanFilter from '@/components/tasks-kanban/components/TasksKanbanFilter.vue';
import { TaskViewElementEnum } from '@/components/tasks-kanban/enums';
import { FilterOptions, SelectedFilterOptions } from '@/components/tasks-kanban/models';
import { ProjectModel } from '@/models';
import { PageRequest } from '@/components/tasks-kanban/adapter';
import PageResponse from '../models/PageResponseModel';
import { CurrentUser } from 'tir-pm-user-core';
import { useTaskKanbanFilter } from '../adapter-service/useTaskKanbanFilter';
import SortColumnRequest from '@/api/Main/dataSource/api/projects/task/i-service/models/task-order/request/SortColumnRequest';
import SubscribeToOrderTaskListRequestModel from '../models/SubscribeToOrderTaskListRequestModel';
import UnSubscribeToOrderTaskListRequestModel from '../models/UnSubscribeToOrderTaskListRequestModel';

const props = defineProps({
  /**id проекта */
  projectId: { type: Number },
  isEnabeledDrag: { type: Boolean },
  /**id юзера отправленный через router */
  routerUserId: { type: Number },
  /**id проекта отправленный через router */
  routerProjectId: { type: Number },
  /**статус пользователя */
  statusFilter: { type: String },
  /**Мои задачи */
  showOnlyMyTasks: { type: Boolean },
});
/**Роутер */
const router = useRouter();
/**API сервис */
const apiService = new TasksOrderPagesAdapterService();

const updateTaskAdapter = UpdateTaskAdapterService.getInstance();

// #region Переменные
const LS_SELECTED_EMPLOYEES_KEY = 'team-tasks-selected-employees';
// #endregion Переменные

const {
  team,
  selectedEmployees,
  performerTaskList,
  filter,
  tasksIds,
  viewTeam,
  $tasksKanbanFilter,
  zmkTaskByPerformerListRequest,
  getIds,
  onUpdateTask,
  onUpdateSortedTasks,
  updateData,
  updateDataSinglePerformer,
  saveZmkTaskByPerformerListRequestLocalstore,
} = useTaskKanbanFilter();

// #region refs
/**Ссылка на шторку задачи */
/**Информация проекта */
const projectInfo = ref<ProjectModel>();
const $taskDrawer = ref<InstanceType<typeof TaskDrawer>>();
/**ID открытого пользователя */
const openedUserId = ref<number>();
/**id пользователя у которого обновили очередность */
const updatedTaskOrderUserId = ref<number>();
/**Статус открытого пользователя */
const userStatus = ref<string>();
/**Был ли применен фильтр */
const isApplyFilter = ref(false);
/**Загрузка */
const isLoading = ref(false);
/**Ссылка на элемент скролла */
const $scroll = ref<InstanceType<typeof TirPmScrollbar>>();
/**Контейнер карточки */
const $container = ref<HTMLElement>();
/**Настройки видимости */
const viewSettings = ref([
  TaskViewElementEnum.Project,
  TaskViewElementEnum.Namespace,
  TaskViewElementEnum.Queue,
  TaskViewElementEnum.Name,
  TaskViewElementEnum.Difficulty,
  TaskViewElementEnum.Number,
  TaskViewElementEnum.Status,
  TaskViewElementEnum.Priority,
  TaskViewElementEnum.EstimateTime,
  TaskViewElementEnum.WorkTime,
]);
provide('popover-scroll-selector', '.project-list-wrapper-content');
// #endregion refs

// #region computed
/**id членов команды */
const teamsId = computed(() => viewTeam.value.map((team) => team.Id));
// #endregion computed

// #region Функции
/**При обновлении выбранных пользователей */
function onUpdateSelectedEmployees() {
  if (!props.routerUserId) openedUserId.value = undefined;
}
/**Обновить поле открытого пользователя */
function updateIsOpened(userId: number, isOpened: boolean) {
  openedUserId.value = isOpened ? userId : undefined;
}
/**Вернуться назад */
const back = () => {
  router.back();
};
/**Обновить выбранных сотрудников */
const updateSelectedEmployees = async (employeesIds: Array<number>, shouldPush?: boolean) => {
  shouldPush
    ? selectedEmployees.value.push(...employeesIds.filter((x) => !!x))
    : (selectedEmployees.value = employeesIds.filter((x) => !!x));

  await getOrderedTaskUser();
  saveSelectedEmployeesInLocalStorage();
};
/**Получение очередности задач для конкретных юзеров с группировкой по задачам/очередям */
const getOrderedTaskUser = async (clear?: boolean) => {
  if (clear) performerTaskList.value = [];

  isLoading.value = true;
  zmkTaskByPerformerListRequest.value.TaskPage = new PageRequest({
    Skip: 0,
    Take: 15,
  });
  zmkTaskByPerformerListRequest.value.PerformerIds = selectedEmployees.value;
  zmkTaskByPerformerListRequest.value.Search = filter.value.Search;
  zmkTaskByPerformerListRequest.value.GlobalStatusIds = getIds(
    $tasksKanbanFilter.value.selectedOptionsFilter.GlobalStatuses,
    $tasksKanbanFilter.value.filterOptions.GlobalStatuses,
  ).map(String);
  await updateData();
  isLoading.value = false;
};
/**Обновить список задач только одного перформера*/
const updateListDataPerformer = async (
  clear?: boolean,
  filterRequest?: ZmkTaskByPerformerListRequest,
) => {
  if (!filterRequest?.PerformerIds.length) return;
  if (clear) performerTaskList.value = [];
  await onUpdateTask(filterRequest?.PerformerIds[0]);
};

/**Получить задачи для конкретного исполнителя */
const getOrderedTaskExecutor = async (pageRequest?: PageRequest, userId?: number) => {
  zmkTaskByPerformerListRequest.value.TaskPage = pageRequest;
  zmkTaskByPerformerListRequest.value.PerformerIds = [userId];
  await updateDataSinglePerformer();
};
const searchOrderedTaskOverviewList = async () => {
  zmkTaskByPerformerListRequest.value.Search = filter.value.Search;
  zmkTaskByPerformerListRequest.value.PerformerIds = selectedEmployees.value;

  if (props.showOnlyMyTasks) {
    zmkTaskByPerformerListRequest.value.PerformerIds = [CurrentUser.Id];
  }
  await updateData();
};

watch(
  () => filter.value.Search,
  async () => {
    await searchOrderedTaskOverviewList();
  },
);

/**Получить выбранных сотрудников из локалстореджа */
const getSelectedEmployees = () => {
  const data = localStorage.getItem(LS_SELECTED_EMPLOYEES_KEY);
  if (data) {
    try {
      const employeesIds = JSON.parse(data);
      if (Array.isArray(employeesIds)) {
        selectedEmployees.value = employeesIds.filter((id) =>
          team.value.some((employee) => employee.Id === id),
        );
      }
    } catch (e) {
      console.error(e);
    }
  }
};

/** Применение фильтра */
const applyFilter = async (
  selectedElements: SelectedFilterOptions,
  filterOption: FilterOptions,
) => {
  isLoading.value = true;
  await updateTaskAdapter.unSubscribeTaskList();
  updateTaskAdapter.OnUpdateTaskEvent.removeListener(onUpdateTask);
  const queueSelected = selectedElements.Queues.filter((q) => q.Id !== 0);

  const x = {
    GlobalStatusIds: [0, 3, 2, 4, 5],
  };
  const filteredStatuses = JSON.stringify(
    [...selectedElements.GlobalStatuses.map((el) => el.GlobalStatusType)].sort(),
  );
  const defaultStatuses = JSON.stringify([...x.GlobalStatusIds].sort());
  if (
    selectedElements.Difficultys.length == 0 &&
    selectedElements.GlobalStatuses.length == 5 &&
    filteredStatuses === defaultStatuses &&
    selectedElements.HiddenElements.length == 0 &&
    selectedElements.Queues?.length == 0 &&
    selectedElements.Namespaces.length == 0 &&
    selectedElements.Projects.length == 0 &&
    selectedElements.Tags.length == 0 &&
    selectedElements.Types.length == 0 &&
    selectedElements.Statuses.length == 0 &&
    selectedElements.Priorities.length == 0
  ) {
    isApplyFilter.value = false;
  } else {
    isApplyFilter.value = true;
  }
  if (selectedElements.Projects.length == 0) {
    zmkTaskByPerformerListRequest.value.ProjectId = undefined;
  } else {
    zmkTaskByPerformerListRequest.value.ProjectId = Number(selectedElements.Projects[0]?.Id);
  }

  zmkTaskByPerformerListRequest.value.NamespaceId =
    selectedElements.Namespaces[0]?.Id.toString() ?? undefined;
  zmkTaskByPerformerListRequest.value.QueueIds = getIds(
    selectedElements.Queues,
    filterOption.Queues,
  ).map(String);
  zmkTaskByPerformerListRequest.value.TaskPage = new PageRequest({
    Skip: 0,
    Take: 15,
  });
  zmkTaskByPerformerListRequest.value.GlobalStatusIds = getIds(
    selectedElements.GlobalStatuses,
    filterOption.GlobalStatuses,
  ).map(String);
  if (props.showOnlyMyTasks) {
    zmkTaskByPerformerListRequest.value.PerformerIds = [CurrentUser.Id];
  } else {
    zmkTaskByPerformerListRequest.value.PerformerIds = selectedEmployees.value;
  }
  if (queueSelected.length > 0) {
    zmkTaskByPerformerListRequest.value.DifficultyIds = getIds(
      selectedElements.Difficultys,
      filterOption.Difficultys,
    ).map(String);
    zmkTaskByPerformerListRequest.value.NamespaceId = String(
      selectedElements.Namespaces[0]?.Id ?? '',
    );

    zmkTaskByPerformerListRequest.value.QueueIds = queueSelected.map((el) => el.Id as string);

    zmkTaskByPerformerListRequest.value.StatusIds = getIds(
      selectedElements.Statuses,
      filterOption.Statuses,
    ).map(Number);
    zmkTaskByPerformerListRequest.value.TagIds = getIds(
      selectedElements.Tags,
      filterOption.Tags,
    ).map(Number);
    zmkTaskByPerformerListRequest.value.TypeIds = getIds(
      selectedElements.Types,
      filterOption.Types,
    ).map(String);
    zmkTaskByPerformerListRequest.value.Search = filter.value.Search;
    zmkTaskByPerformerListRequest.value.DifficultyIds = getIds(
      selectedElements.Difficultys,
      filterOption.Difficultys,
    ).map(String);
  }
  saveZmkTaskByPerformerListRequestLocalstore();
  await updateData();
  isLoading.value = false;
};

/**Сохранить выбранных сотрудников в локалсторедже */
const saveSelectedEmployeesInLocalStorage = () => {
  localStorage.setItem(LS_SELECTED_EMPLOYEES_KEY, JSON.stringify(selectedEmployees.value));
};
/**Открыть шторку с задачей */
const openTaskDrawer = (taskId: number) => {
  $taskDrawer.value?.open(
    new TaskDrawerSettingsModel({
      Mode: 'edit',
      TaskId: taskId,
    }),
  );
};
/**Изменить кол-во элементов на странице */
const changePageStep = async (count: number, userId: number) => {
  const user = performerTaskList.value.find((u) => u.Performer.Id === userId);
  user.PageStep = count;

  await getOrderedTaskExecutor(
    new PageRequest({
      Skip: 0,
      Take: user.PageStep,
    }),
    userId,
  );
};
/**Обновить текущую страницу */
const changeCurrentPage = async (value: number, pageData: PageResponse, userId: number) => {
  if (!pageData || pageData.Current === value) return;
  const user = performerTaskList.value.find((u) => u.Performer.Id === userId);

  await getOrderedTaskExecutor(
    new PageRequest({
      Skip: pageData.Size * (value - 1),
      Take: user.PageStep,
    }),
    userId,
  );
};
/**Получить информацию задач конкретного юзера */
const getTasksUserInfo = async (userID: number, pageData: PageResponse) => {
  const user = performerTaskList.value.find((u) => u.Performer.Id === userID);
  await getOrderedTaskExecutor(
    new PageRequest({
      Skip: 0,
      Take: user.PageStep,
    }),
    userID,
  );
};
/**Получение пользователя */
const getUser = (userId: number) => {
  return viewTeam.value.find((u) => u?.Id == userId);
};
/**Сортировка списка */
const sortListPerformerTask = (_taskId: number, isAddTask: boolean) => {
  performerTaskList.value.forEach((task) => {
    task.TaskList.sort((a, b) => {
      if (isAddTask) {
        if (a.Id === _taskId) return -1;
        if (b.Id === _taskId) return 1;
        return 0;
      } else {
        const aPriority = a.IsHighlighted ? 0 : a.ZmkType === 2 ? 2 : 1;
        const bPriority = b.IsHighlighted ? 0 : b.ZmkType === 2 ? 2 : 1;

        return aPriority - bPriority;
      }
    });
  });
};
/**Подписаться на событие изменения списка очередности */
const subscribeToOrderTasks = async () => {
  await apiService.subscribeToOrderTaskList(
    new SubscribeToOrderTaskListRequestModel({ UserIds: [...teamsId.value, CurrentUser.Id] }),
    (userId: number) => onUpdatePerformerTasks(userId),
  );
};
/**Отписаться от события изменения списка очередности */
const unSubscribeToOrderTasks = async () => {
  await apiService.unSubscribeToOrderTaskList(
    new UnSubscribeToOrderTaskListRequestModel({ UserIds: [...teamsId.value, CurrentUser.Id] }),
    (userId: number) => onUpdatePerformerTasks(userId),
  );
};
/**Установить id юзера у которого обновили список очередности */
const setUpdatedTaskOrderUserId = (idUser: number) => {
  updatedTaskOrderUserId.value = idUser;
};
// #endregion Функции

// Объявляем функцию отдельно
const onUpdatePerformerTasks = (value: number) => {
  updateListDataPerformer(
    false,
    new ZmkTaskByPerformerListRequest({
      PerformerIds: [value],
      GlobalStatusIds: getIds(
        $tasksKanbanFilter.value.selectedOptionsFilter.GlobalStatuses,
        $tasksKanbanFilter.value.filterOptions.GlobalStatuses,
      ).map(String),
    }),
  );
};

/**Обновление определенной задачи */
const updateTask = (updateData) => {
  performerTaskList.value.forEach((element) => {
    const task = element.TaskList.find((task) => task.Id === updateData.Id);
    if (task) Object.assign(task, updateData);
  });
};
/**Спуск до добавленного юзера */
const scrollToAddedUser = async () => {
  if (props.routerUserId) {
    await nextTick();
    const childrenElements = [...$container.value.children];
    const newChildrenElement = childrenElements.find(
      (elem) => Number(elem.id) == props.routerUserId,
    );
    scrollToElementTop($scroll.value.$scroll, newChildrenElement as HTMLElement);
  }
};
/**Удаление задачи */
const removeTask = (taskID: number) => {
  performerTaskList.value = performerTaskList.value.map((element) => ({
    ...element,
    TaskList: element.TaskList.filter((task) => task.Id !== taskID),
  }));
};
/**Удаляет задачу из списка при событии OnRemoveTaskEvent */
const onRemoveTaskHandler = (data: RemoveTaskResponseEventModel) => {
  removeTask(data.TaskId);
};
/**Спуск скрола до элемента */
const scrollToElementTop = ($scroll: HTMLElement, el: HTMLElement) => {
  if (!$scroll || !el) return;

  const scrollOffset = el.offsetTop - $scroll.offsetTop;

  $scroll.scrollTo({
    top: scrollOffset - 40,
    behavior: 'smooth',
  });
};

// #region Хуки жизненного цикла
/**Перед рендером */
onBeforeMount(async () => {
  isLoading.value = true;
  await updateTaskAdapter.subscribeTaskList(tasksIds.value);
  updateTaskAdapter.OnUpdateTaskEvent.on(onUpdateTask);

  const response = await apiService.getTeam();
  if (response.IsSuccess && response.Value) {
    team.value = response.Value;
    getSelectedEmployees();
    zmkTaskByPerformerListRequest.value.IncludeCurrentUserId = props.showOnlyMyTasks;
    zmkTaskByPerformerListRequest.value.TaskPage = new PageRequest({
      Skip: 0,
      Take: 15,
    });
    zmkTaskByPerformerListRequest.value.GlobalStatusIds = getIds(
      $tasksKanbanFilter.value.selectedOptionsFilter.GlobalStatuses,
      $tasksKanbanFilter.value.filterOptions.GlobalStatuses,
    ).map(String);
    zmkTaskByPerformerListRequest.value.Sort = new SortColumnRequest({ SortByTaskId: true });
    zmkTaskByPerformerListRequest.value.PerformerIds = props.showOnlyMyTasks
      ? [CurrentUser.Id]
      : selectedEmployees.value;

    isLoading.value = true;
    if (!props.routerUserId) await updateData();
    isLoading.value = false;
  }
  userStatus.value = props.statusFilter;
  isLoading.value = false;
  if (props.routerUserId) {
    updateIsOpened(props.routerUserId, true);
    await updateSelectedEmployees([props.routerUserId], true);
    scrollToAddedUser();
  }
  await subscribeToOrderTasks();
  updateTaskAdapter.OnRemoveTaskEvent.on(onRemoveTaskHandler);
});
onUnmounted(async () => {
  apiService.unSubscribeOrderPages();
  updateTaskAdapter.OnRemoveTaskEvent.removeListener(onRemoveTaskHandler);
  await updateTaskAdapter.unSubscribeTaskList();
  await updateTaskAdapter.OnUpdateTaskEvent.removeListener(onUpdateTask);
  await unSubscribeToOrderTasks();
});
</script>
<template>
  <TirPmNotificationGroup group="team-tasks" position="top-right" :zIndex="20000" />
  <TitledLayout>
    <template #title v-if="!showOnlyMyTasks"> Задачи моей команды</template>
    <template #title else> Мои задачи</template>
    <TirPmScrollbar ref="$scroll" showOnHover class="team-tasks__scrollbar">
      <div class="team-tasks">
        <div class="team-tasks__header">
          <TirPmButton
            class="team-tasks__back"
            :variant="TirPmButtonVariantEnum.LinkState"
            :size="TirPmButtonSizeEnum.Small"
            @click="back"
          >
            <template #leftSlot>
              <ArrowUturnLeftIcon />
            </template>
            <template #default>Назад </template>
          </TirPmButton>
          <EmployeeMultiselect
            v-if="!showOnlyMyTasks"
            :disabled="isLoading"
            :statusFilter="userStatus"
            :model-value="selectedEmployees"
            :employees="team"
            @update:model-value="updateSelectedEmployees"
          />
          <TasksKanbanFilter
            :showOnlyCurrentIsAdmin="showOnlyMyTasks"
            v-model:view-elements="viewSettings"
            v-model:filter="filter"
            hideStatus
            ref="$tasksKanbanFilter"
            class="team-tasks__filter"
            isSortingAndGroupingHidden
            @applyFilter="applyFilter"
          />
        </div>
        <div ref="$container" class="team-tasks__content">
          <div
            class="team-tasks_item"
            v-for="item in performerTaskList"
            v-if="performerTaskList.length && !isLoading"
            :key="item.Performer.Id"
            :id="String(item.Performer.Id)"
          >
            <TeammateCard
              :showOnlyMyTasks="showOnlyMyTasks"
              :class="{ dragging: !isApplyFilter }"
              :isApplyFilter="isApplyFilter"
              :pageStep="item.PageStep"
              :pageResponse="item.TaskPage"
              :pagesCount="item.TaskPage.Count"
              :user="getUser(item.Performer.Id)"
              :tasks="item.TaskList"
              :is-opened="item.Performer.Id === openedUserId"
              :routerProjectId="routerProjectId"
              :routerUserId="routerUserId"
              :viewElements="viewSettings"
              :filter="filter"
              :tasksIds="tasksIds"
              @sortData="onUpdateSortedTasks(item.Performer.Id, $event)"
              @updateTaskOrder="sortListPerformerTask"
              @removeTask="removeTask"
              @changeCurrentPage="changeCurrentPage"
              @changePageStep="changePageStep"
              @updateListTasksUsers="getTasksUserInfo"
              @update:is-opened="updateIsOpened(item.Performer.Id, $event)"
              @open-task-drawer="openTaskDrawer"
              @updateList="onUpdatePerformerTasks"
              @toggleExecutionTask="setUpdatedTaskOrderUserId"
            />
          </div>
        </div>
        <div v-if="isLoading" class="team-tasks__skeleton-block">
          <TirPmSkeletonLoader width="100%" height="11.25rem" borderRadius=".75rem" />
          <TirPmSkeletonLoader width="100%" height="22.75rem" borderRadius=".75rem" />
        </div>
        <TaskDrawer
          ref="$taskDrawer"
          disable-events
          @onUpdateTask="updateTask"
          @successRemove="removeTask"
        />
      </div>
    </TirPmScrollbar>
  </TitledLayout>
</template>
<style lang="scss" scoped>
.team-tasks {
  display: flex;
  flex-direction: column;
  gap: 1.5625rem;
  &__scrollbar {
    max-height: calc(100vh - 12.4063rem);
  }
  &__content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  &__filter {
    margin-bottom: 0 !important;
  }
  &__skeleton-block {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  &_item {
    position: relative;
    padding: 0.5rem;
    &:not(:last-child)::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -0.75rem;
      display: block;
      width: 100%;
      border-top: 1px solid var(--neutral-30);
    }
  }
  &__header {
    display: flex;
    align-items: center;
    gap: 2.25rem;
    position: sticky;
    top: 0rem;
    left: 0rem;

    background: white;
    z-index: 10;
    padding-bottom: 1rem;
    border-bottom: 0.1rem solid var(--neutral-30);
    flex-wrap: wrap;
  }
}
</style>

<style lang="scss">
.teammate-card_block.dragging {
  .task-card.task-card__order-style-task {
    cursor: move;
  }
}
.team-tasks__back.button--small .button__left-slot {
  width: var(--24);
  height: var(--24);
}
</style>
